/**
 * "CANOPY HAS BEEN UPDATED." (Jeremy, 1 Nov 2026, punch list F173.)
 *
 * After a promotion, anybody with Canopy already open keeps running the old
 * build until they happen to reload. So the page asks, now and then, which
 * build the server is handing out, and when it is not the one this page
 * loaded, a small bar says so with one button, **Reload**. Nothing reloads by
 * itself: somebody halfway through a note would lose it.
 *
 * ## How a newer build is recognised
 *
 * Vite names the app's script after a hash of its contents
 * (`/assets/index-3f9a1c.js`), and `index.html` is the one file that names
 * it. So: fetch `/` with `cache: "no-store"`, read the module script it names,
 * and compare it with the one this page is running. Different means newer.
 *
 * It answers "no" whenever it cannot tell -- a failed fetch, a page that is
 * not the app, or a dev server, whose script is `/src/main.tsx` and never
 * hashed. A wrong "no" leaves somebody on the old build, as before; a wrong
 * "yes" would nag them for ever.
 *
 * ## When
 *
 * Never in the first minute after the page loaded; then every five minutes
 * while the tab is visible, and when it becomes visible again. Once it has
 * said "newer" it stops asking.
 */
import { useEffect, useState } from "react";

/** No check sooner than this after the page loaded. */
export const FIRST_CHECK_AFTER_MS = 60_000;
/** How often to check while the tab is visible. */
export const CHECK_EVERY_MS = 5 * 60_000;

/** A hashed build file: what Vite writes under `/assets/`. */
const HASHED = /\/assets\/[^"'?#\s]+\.js/;

/** The module scripts an HTML page names, in order. */
export function moduleScripts(html: string): string[] {
  const out: string[] = [];
  const tag = /<script\b[^>]*>/gi;
  for (const m of html.matchAll(tag)) {
    const t = m[0];
    if (!/\btype\s*=\s*["']?module/i.test(t)) continue;
    const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(t);
    if (src?.[1]) out.push(src[1]);
  }
  return out;
}

/** The path of a script address, so `https://host/assets/x.js` and `/assets/x.js` compare equal. */
function pathOf(src: string): string {
  try {
    return new URL(src, "http://x").pathname;
  } catch {
    return src;
  }
}

/** The hashed build scripts in a list, as paths. Anything else -- a dev server's `/src/main.tsx` -- is not a build. */
export function buildScripts(srcs: readonly string[]): string[] {
  return srcs.map(pathOf).filter((p) => HASHED.test(p));
}

/**
 * Whether the server is handing out a different build from the one running.
 * False unless BOTH sides name a hashed build: not knowing is not "newer".
 */
export function isNewerBuild(running: readonly string[], served: readonly string[]): boolean {
  const a = buildScripts(running);
  const b = buildScripts(served);
  if (a.length === 0 || b.length === 0) return false;
  return a.slice().sort().join("\n") !== b.slice().sort().join("\n");
}

/** Whether a check is allowed at this moment. */
export function mayCheck(now: number, loadedAt: number, visible: boolean): boolean {
  return visible && now - loadedAt >= FIRST_CHECK_AFTER_MS;
}

/** The scripts this page is running, read once: it never changes without a reload. */
function runningScripts(): string[] {
  return Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')).map(
    (s) => s.getAttribute("src") ?? "",
  );
}

async function servedScripts(): Promise<string[] | null> {
  try {
    const res = await fetch("/", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return moduleScripts(await res.text());
  } catch {
    return null;
  }
}

/** True once the server is handing out a newer build than this page is running. */
export function useNewerBuild(): boolean {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    if (newer) return;
    const loadedAt = Date.now();
    const running = runningScripts();
    // A dev server, or a page with no hashed script: nothing to compare.
    if (buildScripts(running).length === 0) return;
    let stopped = false;
    let asking = false;

    const check = async (): Promise<void> => {
      if (stopped || asking) return;
      if (!mayCheck(Date.now(), loadedAt, document.visibilityState === "visible")) return;
      asking = true;
      const served = await servedScripts();
      asking = false;
      if (!stopped && served && isNewerBuild(running, served)) setNewer(true);
    };

    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [newer]);

  return newer;
}
